import { Injectable } from '@angular/core';
import { AssetsService } from '@sc/data-access-assets';
import { DossierSandboxService } from '@sc/data-access-state-management';
import { StepperStep } from '@sc/ui-components';
import { DeclarationConditions } from '@sc/utils';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomerRouterService } from './router.service';

// NOTE - In the eventuality of a B2BStepperService being created alongside a B2B custom-routing.service, we should make
// the B2CStepperService and the B2BStepperService inherit from the same abstract class, remove the providedIn root and
// provide each one in the right app.module.ts. We could also do the same for the custom-routing services.
@Injectable({
  providedIn: 'root',
})
export class B2CStepperService {
  private readonly fullStepList: Observable<StepperStep[]>;
  private readonly displayStepper: Observable<boolean>;
  private readonly currentStep: Observable<number>;
  private readonly disableStepperLinks: Observable<boolean>;
  private readonly currentStepCompleted: Observable<boolean>;
  private readonly canNavigateCurrentStep: Observable<boolean>;

  constructor(
    private readonly assetsService: AssetsService,
    private readonly routerService: CustomerRouterService,
    private readonly dossierSB: DossierSandboxService,
    private readonly declarationConditions: DeclarationConditions
  ) {
    this.fullStepList = this.assetsService.getRoutingCustomer().pipe(
      map((routingConfig) => {
        const intermediateList = routingConfig.pages
          .map((page) => ({
            pageName: page.name,
            step: page.declarationStep?.pageStep ?? 0,
            disabled: !!page.declarationStep?.isStepDisabled,
          }))
          .filter(
            (pageInfos, pageIndex, pageList) =>
              pageInfos.step > 0 &&
              !pageList.some(
                (pageInfosSearch, pageSearchIndex) =>
                  pageInfos.step === pageInfosSearch.step &&
                  pageIndex > pageSearchIndex
              )
          );

        return intermediateList.map(
          (pageInfos): StepperStep => ({
            titleShort: `CustomerStepper.step${pageInfos.step}.short`,
            titleLong: `CustomerStepper.step${pageInfos.step}.long`,
            disabled: pageInfos.disabled,
            action: () =>
              this.routerService.navigateToPageByName(pageInfos.pageName),
          })
        );
      })
    );

    this.displayStepper = combineLatest([
      this.routerService.currentPage,
      this.dossierSB.dossier$,
    ]).pipe(
      map(
        ([page, dossier]) =>
          page.declarationStep?.pageStep !== 0 &&
          !this.declarationConditions.isAppointmentModified(dossier) &&
          !this.declarationConditions.isAppointmentCancelled(dossier)
      )
    );

    this.currentStep = this.routerService.currentPage.pipe(
      map((page) => page.declarationStep?.pageStep ?? 0)
    );

    this.disableStepperLinks = this.routerService.currentPage.pipe(
      map((page) => !!page.declarationStep?.disableLinks)
    );

    this.currentStepCompleted = this.routerService.currentPage.pipe(
      map((page) => !!page.declarationStep?.isCompleted)
    );

    this.canNavigateCurrentStep = this.routerService.currentPage.pipe(
      map((currentPage) => {
        return !currentPage.declarationStep?.isDefault;
      })
    );
  }

  get fullStepList$() {
    return this.fullStepList;
  }

  get displayStepper$() {
    return this.displayStepper;
  }

  get currentStep$() {
    return this.currentStep;
  }

  get disableStepperLinks$() {
    return this.disableStepperLinks;
  }

  get currentStepCompleted$() {
    return this.currentStepCompleted;
  }

  get canNavigateCurrentStep$() {
    return this.canNavigateCurrentStep;
  }
}
